import React, { useState, useMemo, useCallback } from 'react'
import { Panel, PanelProps, PanelChildFn } from '../components/Panel'

type SetVisibility<D> = (isVisible?: boolean | { isVisible?: boolean; data?: D | null }) => void

export const usePanel = <D,>(params?: {
    onClose?: () => void
}): {
    PanelComponent: (props: PanelProps<D>) => JSX.Element
    isVisible: boolean
    data: D | null
    setPanelVisibility: SetVisibility<D>
} => {
    const [isVisible, setVisibility] = useState(false)
    const [data, setData] = useState<D | null>(null)
    const handleVisibility: SetVisibility<D | null> = useCallback(
        (visibilityParams): void => {
            if (typeof visibilityParams === 'boolean') {
                if (visibilityParams === false) {
                    setData(null)
                }
                setVisibility(visibilityParams)
                return
            }

            const { isVisible: innerVisibilityFlag = !isVisible, data: passedData } =
                visibilityParams || {}

            setVisibility(innerVisibilityFlag)

            if (!!passedData && innerVisibilityFlag === true) {
                setData(passedData)
            }
        },
        [isVisible]
    )

    const Component = useMemo(
        () =>
            ({
                children,
                renderChild,
            }: {
                children?: JSX.Element | JSX.Element[] | undefined
                renderChild?: PanelChildFn<D>
            }) =>
                (
                    <Panel
                        renderChild={renderChild}
                        data={data}
                        isVisible={isVisible}
                        onClose={() => {
                            setData(null)
                            params?.onClose?.()
                            setVisibility(false)
                        }}
                    >
                        {children}
                    </Panel>
                ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data, isVisible]
    )

    return {
        PanelComponent: Component,
        isVisible,
        data,
        setPanelVisibility: handleVisibility,
    }
}
